"use client";

import { InputHTMLAttributes, forwardRef, Ref, useState } from "react";
import * as Form from "@radix-ui/react-form";
import clsx from "clsx";
import CheckboxInput from "@/components/UI/Input/Checkbox/Checkbox";
import SelectInput from "@/components/UI/Input/SelectInput/SelectInput";
import TextInput from "@/components/UI/Input/TextInput/TextInput";
import EyeIcon from "@/app/assets/images/svg/eye.svg";
import EyeSlashIcon from "@/app/assets/images/svg/eye-slash.svg";
import styles from "./FormField.module.scss";

interface Props extends InputHTMLAttributes<HTMLInputElement> {
	name: string;
	label?: string;
	placeholder?: string;
	helperText?: string;
	errorMessage?: string;
	options?: {
		text?: string;
		value?: string | number;
		isLabel?: boolean;
	}[];
	prefix?: string;
	customClass?: string;
	innerIcon?: React.ReactNode;
}

const FormField = forwardRef<HTMLInputElement, Props>((props, ref) => {
	const {
		name,
		label = "",
		value,
		placeholder = label,
		helperText,
		errorMessage,
		type,
		options,
		prefix,
		onChange,
		customClass,
		innerIcon,
	} = props;

	const inputHTML = () => {
		switch (type) {
			case "checkbox": {
				return (
					<CheckboxInput
						{...props}
						checked={value ? true : false}
						label={label}
					/>
				);
			}
			case "select": {
				return (
					<SelectInput
						ref={ref as Ref<HTMLButtonElement>}
						label={label}
						placeHolder={placeholder}
						options={options}
						prefix={prefix}
						errorMessage={errorMessage}
						onChange={onChange}
						defaultValue={value?.toString()}
					/>
				);
			}
			case "password": {
				const [showPassword, setShowPassword] = useState(false);

				const toggleShowPasswordIcon = showPassword ? (
					<EyeIcon onClick={() => setShowPassword(!showPassword)} />
				) : (
					<EyeSlashIcon onClick={() => setShowPassword(!showPassword)} />
				);

				return (
					<TextInput
						{...props}
						type={showPassword ? "text" : "password"}
						ref={ref}
						className={styles.input}
						value={value}
						errorMessage={errorMessage}
						placeholder={placeholder}
						innerIcon={toggleShowPasswordIcon}
					/>
				);
			}
			default: {
				return (
					<TextInput
						{...props}
						ref={ref}
						className={styles.input}
						value={value}
						errorMessage={errorMessage}
						placeholder={placeholder}
						innerIcon={innerIcon}
					/>
				);
			}
		}
	};

	return (
		<Form.FormField
			name={name}
			className={clsx([styles.formField, customClass])}
		>
			{label && type !== "checkbox" && (
				<Form.Label className={styles.label}>
					{label} {props.required && <span className={styles.asterisk}>*</span>}
				</Form.Label>
			)}
			<Form.Control asChild>{inputHTML()}</Form.Control>
			{errorMessage && (
				<Form.Message className={styles.errorMessage}>
					{errorMessage}
				</Form.Message>
			)}
			{helperText && (
				<Form.Message className={styles.helpText}>{helperText}</Form.Message>
			)}
		</Form.FormField>
	);
});

export default FormField;
